/**
 * Session-change view projection (RFC 0235: session version control).
 *
 * Derives a session's code-change history from `mod.session_change` extension
 * events (RFC 0215 §3.8) in causal order. Each event LINKS to a VCS change (its
 * ref + touched paths); the log never carries file content, so this projection
 * reads only the linkage. Peers without a session-change handler load these
 * entries, preserve them, and simply do not project them here.
 */

import { topoOrder } from './projection-order'
import type { LogDoc } from './types'
import {
  SESSION_CHANGE_EXTENSION,
  SESSION_DISCARDED_EXTENSION,
  SESSION_MERGED_EXTENSION,
} from './types'

export interface SessionChangeRef {
  sessionId: string
  /** The linked VCS change (re-nested from the flat on-doc scalars). */
  vcsRef: { backend: string; commit: string; ref: string }
  /** Paths touched by the change. Never file content. */
  files: string[]
  /** When the change was recorded (millis). */
  at: number
}

/**
 * All session-change links on the log, oldest first. The session's VCS history
 * as seen from the log: what changed (paths) and where the bytes live (vcsRef).
 */
export function projectSessionChanges(doc: LogDoc): SessionChangeRef[] {
  const out: SessionChangeRef[] = []
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'extension') continue
    const { extensionType, payload } = p.payload
    if (extensionType !== SESSION_CHANGE_EXTENSION) continue

    // Opaque payload (Record<string, unknown>); coerce flat scalars. `files`
    // round-trips as an Automerge list, so normalise each element to a string.
    const rawFiles = payload.files
    const files = Array.isArray(rawFiles) ? rawFiles.map((f) => String(f)) : []
    out.push({
      sessionId: payload.sessionId != null ? String(payload.sessionId) : '',
      vcsRef: {
        backend: payload.vcsBackend != null ? String(payload.vcsBackend) : '',
        commit: payload.vcsCommit != null ? String(payload.vcsCommit) : '',
        ref: payload.vcsBranch != null ? String(payload.vcsBranch) : '',
      },
      files,
      at: payload.at != null ? Number(payload.at) : 0,
    })
  }
  return out
}

export interface SessionLifecycle {
  /** The session's terminal VCS state. `active` until disregarded or merged. */
  status: 'active' | 'discarded' | 'merged'
  /** When the terminal event was recorded (millis), if any. */
  at?: number
  /** The merge commit, when status is 'merged'. */
  mergeCommit?: string
  /** The base merged into, when status is 'merged'. */
  into?: string
}

/**
 * The session's terminal VCS lifecycle from `mod.session_discarded` /
 * `mod.session_merged` events. The last terminal event wins (causal order); a
 * session with neither is `active`.
 */
export function projectSessionLifecycle(doc: LogDoc): SessionLifecycle {
  let state: SessionLifecycle = { status: 'active' }
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'extension') continue
    const { extensionType, payload } = p.payload
    if (extensionType === SESSION_DISCARDED_EXTENSION) {
      state = {
        status: 'discarded',
        at: payload.at != null ? Number(payload.at) : 0,
      }
    } else if (extensionType === SESSION_MERGED_EXTENSION) {
      state = {
        status: 'merged',
        at: payload.at != null ? Number(payload.at) : 0,
        mergeCommit:
          payload.vcsCommit != null ? String(payload.vcsCommit) : undefined,
        into: payload.vcsInto != null ? String(payload.vcsInto) : undefined,
      }
    }
  }
  return state
}
